import { useMutation } from '@apollo/client'
import { Editor } from '@tiptap/react'
import { BlastCandidatesMutationVariables } from '__generated__/graphql'
import { BlastCandidates } from 'recruiter/graphql/rosters.gql'
import useToast from 'shared/components/Toast/useToast'
import { formatGraphQLError } from 'shared/utils'
import { ApolloError, isApolloError } from '@apollo/client'
import { FormValues } from './blastValidationSchema'
import {
    makeMergeFieldRemover,
    makeMergeFieldExtractor,
    MergeFieldData,
} from './makeMergeUtils'

export const useBlastRosterCandidates = (
    editor: Editor | null | undefined,
    mergeFieldData: MergeFieldData | undefined,
    candidateIds: string[],
    rosterId: string,
    formValues: FormValues,
    handleCancel: (data?: any) => void
) => {
    const toast = useToast()
    const [blastCandidatesMutation] = useMutation(BlastCandidates)

    const removeMergeFields = makeMergeFieldRemover(mergeFieldData)
    const extractUsedMergeFields = makeMergeFieldExtractor(mergeFieldData)

    return async () => {
        const htmlBody = editor?.getHTML() || ''
        const rawBody = editor?.getText() || ''
        const contentHtml = removeMergeFields(htmlBody)
        const mergeFieldsPresent = extractUsedMergeFields(contentHtml)
        const isEmailBodyEmpty =
            editor?.isEmpty && mergeFieldsPresent.length === 0
        const isSuppliedCandidates = candidateIds.length > 0

        let errors = [
            isEmailBodyEmpty ? 'Email body is empty' : '',
            isSuppliedCandidates ? '' : 'No candidates selected',
        ]

        if (errors.some(Boolean)) {
            const errorsText = errors.filter((i) => i !== '').join(', ')

            toast({
                title: 'Error',
                severity: 'error',
                message: `Form field errors sending blast: ${errorsText}`,
            })
            return
        }

        const variables: BlastCandidatesMutationVariables = {
            subject: formValues.subject,
            contentRaw: rawBody,
            contentHtml,
            rosterId,
            candidateIds,
            emailPreferenceOrder: formValues.emailPreferenceOrder
                .filter((item) => item.enabled)
                .map((item) => item.preference),
            jobBoardInput: formValues.jobBoard
                ? {
                      name: formValues.name || '',
                      description: formValues.description || '',
                      titleIds: formValues.titles?.map((t: any) => t.id) || [],
                      sectorsIds:
                          formValues.sectors?.map((s: any) => s.id) || [],
                      companyOfficesIds:
                          formValues.locations?.map((l: any) => l.id) || [],
                  }
                : undefined,
        }

        try {
            await blastCandidatesMutation({
                variables,
                onCompleted: (data: any) => {
                    const unreachableData = data.blastCandidates?.data
                    handleCancel(unreachableData)
                    toast({
                        title: 'Success!',
                        severity: 'success',
                        message:
                            data.blastCandidates?.message ??
                            'Blast sent successfully',
                    })
                },
                onError: (error: any) => {
                    console.error(error)
                    toast({
                        title: 'Error!',
                        severity: 'error',
                        message:
                            error?.message ??
                            'Something went wrong. Please try again later.',
                    })
                },
            })
        } catch (e) {
            if (isApolloError(e as Error)) {
                toast({
                    title: 'Error',
                    message: formatGraphQLError(
                        e as ApolloError,
                        'Something went wrong.'
                    ),
                    severity: 'error',
                })
            } else {
                toast({
                    title: 'Error',
                    message: 'Something went wrong.',
                    severity: 'error',
                })
            }
        }
    }
}
